<script setup lang="ts">
    import { useCommandContext } from './context';

    defineOptions({ inheritAttrs: false });

    const { t } = useI18n();
    const attrs = useAttrs();
    const context = useCommandContext('CommandList');
    const element = useTemplateRef<HTMLElement>('element');

    onMounted(() => (context.list.value = element.value));
    onBeforeUnmount(() => (context.list.value = null));

    /* Without an item shown it is a plain box for CommandEmpty, so it drops the listbox role and its name. */
    const listAttrs = computed(() => {
        const { 'aria-label': label, 'aria-labelledby': labelledBy, ...rest } = attrs;
        if (!context.hasVisibleItems.value) return rest;
        return { ...rest, role: 'listbox', 'aria-labelledby': labelledBy, 'aria-label': labelledBy ? label : (label ?? t('SUGGESTIONS')) };
    });
</script>

<template>
    <!-- `tabindex="-1"`: focus stays in the input, and a scrolling box would otherwise be a tab stop of its own -->
    <div
        v-bind="listAttrs"
        :id="context.listId"
        ref="element"
        tabindex="-1"
        data-slot="command-list"
        class="ui-command-list"
        @mousedown.prevent
    >
        <slot />
    </div>
</template>

<style scoped>
    @layer components {
        .ui-command-list {
            max-height: min(24rem, 60vh);
            padding: 8px;
            overflow-y: auto;
            overscroll-behavior: contain;
            outline: none;
            scroll-padding-block: 8px;
        }

        /* Nothing to show: no item found and no CommandEmpty */
        .ui-command-list:not(:has([data-slot='command-item']:not([hidden]), [data-slot='command-empty'])) {
            display: none;
        }
    }
</style>

<i18n lang="yaml">
en:
    SUGGESTIONS: Suggestions
nl:
    SUGGESTIONS: Suggesties
</i18n>
